import commonSetting from '@intelligence/components/DownloadCommon/DownloadPage/configs/commonSetting';
import {
  metricTitleMap,
  pcConsoleGameMetric,
  ampereMetricCommon,
  ampereMetricDay,
  ampereMetricMonth,
} from '@intelligence/components/DownloadCommon/DownloadPage/common/baseConfig';
import { getMobileDetailDownloadTitle } from '@intelligence/components/DownloadCommon/DownloadPage/common/baseSetting';
import { pipe } from '@intelligence/components/DownloadCommon/DownloadPage/utils';
import { isArray, union, cloneDeep } from 'lodash-es';
import { getPlatformCacaderValues } from '@intelligence/pages/TopCharts/_config/topchartFilterCfg';
import {
  setGameTitle,
  setItem,
  setMetricTitle,
  setPercentForPCConsole,
  setTitleByCompanyShow,
  setTitlesByDateMethod,
  setTitleByGenreShow,
  setTitleByPlatform,
  setTitleByRegionMethod,
  setTitleByReleaseDateShow,
  setTitleBySteamIdShow,
} from '@intelligence/components/DownloadCommon/DownloadPage/common/titleSettingAtom';
import {
  getFileFormatByKey,
  getPlatformByKey,
  getRegionValueByKey,
  setMetricsByList,
  setTimingByDateMethod,
} from '@intelligence/components/DownloadCommon/DownloadPage/common/summarySettingAtom';
import { allMetricValues } from '@intelligence/pages/TopCharts/MobileGames/components/GamesDetails/KPITrends/components/MetricsSelection/config';

interface IMergedSourceMetric {
  Source: string;
  Metric: string;
  Segment?: string;
}

/**
 * Keep TopCharts download header consistent with the table header.
 *
 * Note:
 * - The table uses i18n keys like `dbi_metric_vgi_price` whose English text includes "VGI".
 * - The base `metricTitleMap` may map `est_price` to "Steam Price($)" (without "VGI").
 * - We do NOT "force add" a "VGI " prefix programmatically; instead we map specific metric keys
 *   to the exact table header strings.
 */
const VGI_METRIC_TITLE_OVERRIDES: Record<string, string> = {
  pcu: 'VGI Steam PCU',
  acu: 'VGI Steam ACU',
  steamfollowers: 'Steam Followers',
  steamreviews: 'VGI Steam Reviews',
  est_revenue: 'VGI Steam Premium Revenue($)',
  est_cumulative_revenue: 'VGI Steam Cumulative Revenue($)',
  est_units: 'VGI Steam Units',
  est_cumulative_units: 'VGI Steam Cumulative Units',
  est_price: 'VGI Steam Price($)',
  weighted_price: 'VGI Steam Weighted Price($)',
  lifetime_weighted_price: 'VGI Steam Lifetime Weighted Price($)',
  wishlists: 'VGI Steam Daily Wishlist',
  wishlists_total: 'VGI Steam Lifetime Wishlist',
  playtime: 'VGI Steam Avg. Playtime(mins)',
  players_by_country: 'VGI Player by Country',
};

const newMetricMap = {
  ...metricTitleMap,
  ...pcConsoleGameMetric,
  ...ampereMetricCommon,
  ...VGI_METRIC_TITLE_OVERRIDES,
};
const sourceDownloadSegment: Record<string, string[]> = {
  mscience: [
    'All',
    'Full Game',
    'DLC: All Other',
    'DLC: Expansion',
    'DLC: Season Pass',
    'DLC: Battle Pass',
    'MTX',
    'Subscription',
  ],
  gsd: ['All', 'Full Game', 'Game', 'Add-On', 'In-Game Currency'],
  gamalytic: ['All'],
  vginsights: ['All'],
  alinea: ['All'],
  // steamsales: ['All'], // 用于 Compare 页面下载弹窗中合并的 Steam Data 分组
  newzoo: ['All'],
  newzoo_weight: ['All'],
  npd: ['All'],
};
const merges = (data, t) => {
  const first = Object.keys(t);
  first.forEach((item) => {
    if (isArray(data[item])) {
      data[item] = union(data[item], t[item]);
    } else {
      data[item] = merges(data[item], t[item]);
    }
  });
  return data;
};
const sortMergedData = (metrics?: IMergedSourceMetric[]): IMergedSourceMetric[] => {
  if (!Array.isArray(metrics) || metrics.length === 0) return [];
  const clonedMetrics = cloneDeep(metrics);
  clonedMetrics.sort((a, b) => {
    const aIndex = allMetricValues.indexOf(a?.Metric);
    const bIndex = allMetricValues.indexOf(b?.Metric);
    if (aIndex === -1 && bIndex === -1) return 0;
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;
    return aIndex - bIndex;
  });
  return clonedMetrics;
};
const mergeData = function (params: Record<string, any> = {}) {
  const result: IMergedSourceMetric[] = [];

  let segments = sourceDownloadSegment;
  // 如果是新segment且包含metric_condition字段，则做过滤
  if (params?.metric_condition) {
    const newMetricCondition = params.metric_condition || {};
    const newMetricObj: any = { ...newMetricCondition };
    Object.entries(newMetricObj)?.forEach(([key, value]) => {
      newMetricObj[key] = value?.segments || [];
    });
    segments = {
      ...segments,
      ...newMetricObj,
    };
  }
  // if (params?.segment_method !== 'aggregated') {
  //   segments = params?.segmentsByDownload;
  // }
  let metrics = params.source_metrics;
  
  // 在 Compare 页面中，外部筛选器的 gamalytic 和 vginsights 需要映射到下载配置的 steamsales
  // 因为下载弹窗使用 COMBINE_GAMALYTIC_STEAM_DATA_METRICS_COMPARE 配置，将它们合并为 "Steam Data"
  // if (metrics?.gamalytic || metrics?.vginsights) {
  //   const steamsalesMetrics = [
  //     ...(metrics?.gamalytic || []),
  //     ...(metrics?.vginsights || [])
  //   ];
  //   metrics = {
  //     ...metrics,
  //     steamsales: steamsalesMetrics
  //   };
  //   // 移除原始的 gamalytic 和 vginsights key，因为它们已经合并到 steamsales 了
  //   delete metrics.gamalytic;
  //   delete metrics.vginsights;
  // }
  
  // 遍历 source_metric 中的每个键
  Object.keys(metrics).forEach((key) => {
    // 获取当前键对应的 metrics 数组
    const currentMetrics = metrics[key] || [];
    // 获取当前键对应的 segments 数组
    const currentSegments = segments[key] || [];

    // 遍历 metrics 数组
    currentMetrics?.forEach((metric: any) => {
      // 遍历 segments 数组
      if (params.segment_method === 'aggregated') {
        result.push({ Metric: metric, Source: '' });
      } else {
        if (currentSegments?.length) {
          currentSegments?.forEach((segment: any) => {
            // 将结果添加到结果数组中
            result.push({ Metric: metric, Segment: segment, Source: '' });
          });
        } else {
          result.push({ Metric: metric, Segment: '', Source: '' });
        }
      }
    });
  });

  return sortMergedData(result);
};
const getDownloadParams = (params: Record<string, any> = {}) => {
  const metricList = mergeData(params);
  const pipeFront = pipe(
    setGameTitle,
    setTitlesByDateMethod(params?.isAlignToRelease, params?.downlaod_date_method),
    setTitleByCompanyShow(params.company_show),
    setTitleByGenreShow(params.genre_show),
    setTitleByReleaseDateShow(params.release_date_show),
    setTitleBySteamIdShow(params.steam_id_show),
    setTitleByRegionMethod(params.region_method),
    setTitleByPlatform(params.device_method),
  );

  let newMetricMapTemp = newMetricMap;
  if (params.granularity === 'daily') {
    newMetricMapTemp = {
      ...newMetricMapTemp,
      ...ampereMetricDay,
    };
  } else {
    newMetricMapTemp = {
      ...newMetricMapTemp,
      ...ampereMetricMonth,
    };
  }

  const pipeInLoop = pipe(
    setItem,
    setMetricTitle(newMetricMapTemp, params.date_type, 'isObject'),
    setPercentForPCConsole(params.ratio_show),
  );

  const excelTitleList = getMobileDetailDownloadTitle(pipeFront, metricList, pipeInLoop);
  const sheetNames = params?.date_method !== 'aggregated' ? 'Stats-Monthly' : 'Stats';
  return {
    sheetName: [sheetNames],
    [sheetNames]: excelTitleList,
  };
};

const getMarket = (params: any) => {
  const { markets, regionOptions } = params || {};
  if (!markets?.length) return undefined;
  if (markets?.length && !markets?.includes('global')) return markets;
  const marketList = [];
  if (regionOptions) {
    for (const item of regionOptions) {
      const itemMarket = item.country_set?.map((item: any) => item.country_code);
      marketList?.push(...itemMarket);
    }
  }
  marketList?.unshift('global');
  return marketList?.flat();
};
const getFParams = (params: any) => {
  const { region_method, device_method, segment_method, downlaod_date_method } = params;
  return {
    b_group_by_segment: segment_method !== 'aggregated',
    b_group_by_market: region_method === 'market',
    b_group_by_platform: device_method === 'separate',
    b_group_by_device: device_method === 'device',
    b_group_by_region: region_method === 'region',
    b_group_by_date: downlaod_date_method !== 'aggregated',
    b_growth_ratio: params.ratio_show === 1 || params.ratio_show === '1',
    company_show: params?.company_show === 1 || params?.company_show === '1',
    genre_show: params?.genre_show === 1 || params?.genre_show === '1',
    steam_id_show: params?.steam_id_show === 1 || params?.steam_id_show === '1',
    release_date_show: params?.release_date_show === 1 || params?.release_date_show === '1',
    segments: params?.segments || ['All'],
    markets: region_method === 'aggregated' ? params?.markets : getMarket(params),
    metric_condition:
      segment_method !== 'aggregated'
        ? merges(
            params.metric_condition,
            getPlatformCacaderValues(params?.segmentsByDownload || {}),
          )
        : params.metric_condition,
    adjust_metric: params?.adjust_metric ?? true,
  };
};
const getSummarySheet = (params: Record<string, any>) => {
  let newMetricMapTemp = newMetricMap;
  if (params.granularity === 'daily') {
    newMetricMapTemp = {
      ...newMetricMapTemp,
      ...ampereMetricDay,
    };
  } else {
    newMetricMapTemp = {
      ...newMetricMapTemp,
      ...ampereMetricMonth,
    };
  }

  const list: any[] = [];
  const { region_method, file_format, source_metrics } = params || {};
  const setSummarySheet = pipe(
    getPlatformByKey(params),
    setTimingByDateMethod(params),
    setMetricsByList(source_metrics, newMetricMapTemp),
    getRegionValueByKey(region_method),
    getFileFormatByKey(file_format),
  );
  setSummarySheet(list);
  return list;
};
const granularityObject: Record<string, string> = {
  daily: 'D',
  weekly: 'W',
  monthly: 'M',
};
const getFileName = (params: any) => {
  const {
    start_release_time,
    start_date,
    end_release_time,
    end_date,
    granularity,
    new_style_donwload_file_name,
  } = params;
  const gItem = granularityObject[granularity];
  if (start_date && end_date) {
    return `${new_style_donwload_file_name}_${start_date}_${end_date}`;
  }
  if (start_release_time && end_release_time) {
    return `${new_style_donwload_file_name}_Release_${gItem}${start_release_time}_${gItem}${end_release_time}`;
  }
  return `${new_style_donwload_file_name}`;
};
export default (params: Record<string, any>) => {
  params = JSON.parse(JSON.stringify(params));
  return {
    ...commonSetting,
    isUsingNewMethod: false,
    $$fileName: getFileName(params),
    downloadFetchApi: '/api/v1/intelligence_pc/compare/pcConsoleMetricData',
    dataKey: undefined,
    summarySheet: getSummarySheet(params),
    regionOptions: params?.regionOptions,
    byDate: params?.downlaod_date_method !== 'aggregated',
    granularity: granularityObject[params?.granularity],
    isRelease: !!(params?.end_release_time && params?.start_release_time),
    fParams: getFParams(params),
    fetchOnlyOnce: true,
    ...getDownloadParams(params),
  };
};
